import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { auth, db, functions } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { QRCode } from 'react-qr-code';
import { forceSignOutStaff } from '../utils/staffSessionGuard';
import './FreeTicketCreator.css';

const EMAIL_SPLIT_REGEX = /[\s,;]+/;

function parseEmailList(value) {
  return [...new Set(
    String(value || '')
      .split(EMAIL_SPLIT_REGEX)
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean)
  )];
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function mapCallableError(error) {
  const detail = error?.message || error?.details || '';
  if (typeof detail === 'string' && detail) return detail;
  return 'No se pudo completar el envío de email.';
}

const FreeTicketCreator = () => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [qrData, setQrData] = useState([]);
  const [name, setName] = useState('');
  const [manualEmails, setManualEmails] = useState('');
  const [emailCustomers, setEmailCustomers] = useState([]);
  const [selectedCustomerEmails, setSelectedCustomerEmails] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [recipientLists, setRecipientLists] = useState([]);
  const [recipientListsLoading, setRecipientListsLoading] = useState(false);
  const [recipientListsError, setRecipientListsError] = useState('');
  const [selectedRecipientListId, setSelectedRecipientListId] = useState('');
  const [selectedListEmails, setSelectedListEmails] = useState([]);
  const [emailToolsLoaded, setEmailToolsLoaded] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [recipientListSaving, setRecipientListSaving] = useState(false);

  const selectedTicketCount = selectedProducts.reduce((total, product) => total + product.quantity, 0);
  const manualEmailList = useMemo(() => parseEmailList(manualEmails), [manualEmails]);
  const emailRecipients = useMemo(
    () => [...new Set([...manualEmailList, ...selectedCustomerEmails, ...selectedListEmails])],
    [manualEmailList, selectedCustomerEmails, selectedListEmails]
  );
  const invalidManualEmails = useMemo(
    () => manualEmailList.filter((email) => !isValidEmail(email)),
    [manualEmailList]
  );
  const normalizedCustomerSearch = customerSearch.trim().toLowerCase();
  const filteredEmailCustomers = useMemo(() => {
    if (!normalizedCustomerSearch) return emailCustomers;
    return emailCustomers.filter((customer) => {
      const haystack = `${customer.name || ''} ${customer.email || ''} ${customer.source || ''}`.toLowerCase();
      return haystack.includes(normalizedCustomerSearch);
    });
  }, [emailCustomers, normalizedCustomerSearch]);
  const selectedRecipientList = useMemo(
    () => recipientLists.find((list) => list.id === selectedRecipientListId) || null,
    [recipientLists, selectedRecipientListId]
  );
  const recipientDetails = useMemo(() => {
    const customersByEmail = new Map(emailCustomers.map((customer) => [customer.email, customer]));
    const listRecipientsByEmail = new Map(
      (selectedRecipientList?.recipients || []).map((recipient) => [recipient.email, recipient])
    );
    return emailRecipients.map((email) => {
      const customer = customersByEmail.get(email);
      if (customer) {
        return {
          email,
          name: customer.name || email,
          source: customer.source || 'Cliente'
        };
      }
      const listedRecipient = listRecipientsByEmail.get(email);
      if (listedRecipient) {
        return {
          email,
          name: listedRecipient.name || email,
          source: listedRecipient.source || selectedRecipientList?.name || 'Lista'
        };
      }
      return { email, name: email, source: 'Manual' };
    });
  }, [emailCustomers, emailRecipients, selectedRecipientList]);
  const selectedListContactDetails = useMemo(() => {
    const customersByEmail = new Map(emailCustomers.map((customer) => [customer.email, customer]));
    const listRecipientsByEmail = new Map(
      (selectedRecipientList?.recipients || []).map((recipient) => [recipient.email, recipient])
    );
    return selectedListEmails.map((email) => {
      const customer = customersByEmail.get(email);
      const listedRecipient = listRecipientsByEmail.get(email);
      return {
        email,
        name: customer?.name || listedRecipient?.name || email,
        source: customer?.source || listedRecipient?.source || selectedRecipientList?.name || 'Lista'
      };
    });
  }, [emailCustomers, selectedListEmails, selectedRecipientList]);

  const loadRecipientLists = useCallback(async () => {
    setRecipientListsLoading(true);
    setRecipientListsError('');
    try {
      const listRecipientLists = httpsCallable(functions, 'listFreeTicketRecipientLists');
      const result = await listRecipientLists({});
      setRecipientLists(Array.isArray(result.data?.lists) ? result.data.lists : []);
    } catch (error) {
      console.error('Error cargando listas de destinatarios:', error);
      setRecipientListsError(mapCallableError(error));
    } finally {
      setRecipientListsLoading(false);
    }
  }, []);

  const loadEmailCustomers = useCallback(async () => {
    setCustomersLoading(true);
    setCustomersError('');
    try {
      const listCustomers = httpsCallable(functions, 'listFreeTicketEmailCustomers');
      const result = await listCustomers({});
      setEmailCustomers(Array.isArray(result.data?.customers) ? result.data.customers : []);
    } catch (error) {
      console.error('Error cargando clientes para email:', error);
      setCustomersError(mapCallableError(error));
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  const ensureCurrentStaffCanCreateFreeTickets = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      await forceSignOutStaff('session');
      return false;
    }

    try {
      await user.getIdToken(true);
      const profileFn = httpsCallable(functions, 'staffGetOwnProfile');
      const res = await profileFn({});
      const profile = res.data || {};
      const role = String(profile.role || '').toLowerCase();
      const allowedFeatures = Array.isArray(profile.allowedFeatures) ? profile.allowedFeatures : [];
      const canCreateFreeTickets =
        profile.exists === true &&
        profile.authDisabled !== true &&
        (role === 'admin' || (role === 'staff' && allowedFeatures.includes('freeTickets')));

      if (canCreateFreeTickets) {
        return true;
      }

      await forceSignOutStaff(profile.authDisabled === true ? 'disabled' : 'permission');
      return false;
    } catch (error) {
      const code = error?.code || '';
      const shouldSignOut =
        code === 'functions/unauthenticated' ||
        code === 'unauthenticated' ||
        code === 'functions/permission-denied' ||
        code === 'permission-denied';

      if (shouldSignOut) {
        await forceSignOutStaff(code.includes('unauthenticated') ? 'session' : 'permission');
        return false;
      }

      console.error('Error validating free ticket staff session:', error);
      setMessageType('error');
      setSuccessMessage('No se pudo verificar si tu usuario sigue habilitado. Volvé a intentar.');
      return false;
    }
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsSnapshot = await getDocs(collection(db, 'products'));
        const otherProductsSnapshot = await getDocs(collection(db, 'otherProducts'));

        const allProducts = [
          ...productsSnapshot.docs.map(doc => ({ id: doc.id, collection: 'products', ...doc.data() })),
          ...otherProductsSnapshot.docs.map(doc => ({ id: doc.id, collection: 'otherProducts', ...doc.data() })),
        ];

        setProducts(allProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    fetchProducts();
  }, []);

  const handleLoadEmailTools = async () => {
    const canCreateFreeTickets = await ensureCurrentStaffCanCreateFreeTickets();
    if (!canCreateFreeTickets) return;

    await Promise.all([loadEmailCustomers(), loadRecipientLists()]);
    setEmailToolsLoaded(true);
  };

  const handleCreateFreeTickets = async () => {
    if (selectedProducts.length === 0) {
      setMessageType('error');
      setSuccessMessage('Por favor, selecciona al menos un producto.');
      return;
    }
    if (invalidManualEmails.length > 0) {
      setMessageType('error');
      setSuccessMessage(`Revisá estos emails: ${invalidManualEmails.join(', ')}`);
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    setQrData([]);

    try {
      const canCreateFreeTickets = await ensureCurrentStaffCanCreateFreeTickets();
      if (!canCreateFreeTickets) return;

      const createTickets = httpsCallable(functions, 'createFreeTicketsSecure');
      const result = await createTickets({
        name,
        products: selectedProducts.map((product) => ({
          id: product.id,
          collection: product.collection,
          quantity: product.quantity,
        })),
        recipients: emailRecipients,
        recipientDetails,
      });
      const createdCartItems = Array.isArray(result.data?.cartItems) ? result.data.cartItems : [];
      setQrData(createdCartItems);
      setMessageType('success');
      setSuccessMessage(
        emailRecipients.length > 0
          ? `¡Tickets creados y enviados a ${result.data?.sentCount || emailRecipients.length} email(s)!`
          : '¡Tickets creados con éxito!'
      );
      setManualEmails('');
      setSelectedCustomerEmails([]);
      setSelectedRecipientListId('');
      setSelectedListEmails([]);
      setSelectedProducts([]);
      setName('');
    } catch (error) {
      console.error('Error al crear los tickets gratuitos:', error);
      setMessageType('error');
      setSuccessMessage(mapCallableError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = (productId) => {
    const product = products.find(p => `${p.collection}:${p.id}` === productId);
    if (product && !selectedProducts.find(p => p.id === product.id && p.collection === product.collection)) {
      setSelectedProducts([...selectedProducts, { ...product, quantity: 1 }]);
      setSuccessMessage('');
    }
  };

  const handleRemoveFromCart = (productId, collectionName) => {
    setSelectedProducts(selectedProducts.filter(product => !(product.id === productId && product.collection === collectionName)));
  };

  const handleQuantityChange = (productId, collectionName, quantity) => {
    const normalizedQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

    setSelectedProducts(
      selectedProducts.map(product =>
        product.id === productId && product.collection === collectionName ? { ...product, quantity: normalizedQuantity } : product
      )
    );
  };

  const toggleCustomerEmail = (email) => {
    setSelectedCustomerEmails((prev) => {
      if (prev.includes(email)) {
        return prev.filter((item) => item !== email);
      }
      return [...prev, email];
    });
  };

  const handleRecipientListChange = (listId) => {
    setSelectedRecipientListId(listId);
    const list = recipientLists.find((item) => item.id === listId);
    setSelectedListEmails(list?.recipientEmails || []);
  };

  const handleSaveRecipientList = async () => {
    if (!newListName.trim()) {
      setMessageType('error');
      setSuccessMessage('Ingresá un nombre para la lista de destinatarios.');
      return;
    }
    if (emailRecipients.length === 0) {
      setMessageType('error');
      setSuccessMessage('Seleccioná o ingresá al menos un email antes de guardar la lista.');
      return;
    }
    if (invalidManualEmails.length > 0) {
      setMessageType('error');
      setSuccessMessage(`Revisá estos emails: ${invalidManualEmails.join(', ')}`);
      return;
    }

    setRecipientListSaving(true);
    try {
      const canCreateFreeTickets = await ensureCurrentStaffCanCreateFreeTickets();
      if (!canCreateFreeTickets) return;

      const saveList = httpsCallable(functions, 'saveFreeTicketRecipientList');
      const result = await saveList({
        name: newListName.trim(),
        recipients: recipientDetails
      });
      await loadRecipientLists();
      setSelectedRecipientListId(result.data?.id || '');
      setSelectedListEmails(result.data?.recipientEmails || emailRecipients);
      setNewListName('');
      setMessageType('success');
      setSuccessMessage('Lista de destinatarios guardada.');
    } catch (error) {
      console.error('Error guardando lista de destinatarios:', error);
      setMessageType('error');
      setSuccessMessage(mapCallableError(error));
    } finally {
      setRecipientListSaving(false);
    }
  };

  const handleUpdateRecipientList = async () => {
    if (!selectedRecipientListId || !selectedRecipientList) return;
    if (emailRecipients.length === 0) {
      setMessageType('error');
      setSuccessMessage('La lista necesita al menos un email.');
      return;
    }
    if (invalidManualEmails.length > 0) {
      setMessageType('error');
      setSuccessMessage(`Revisá estos emails: ${invalidManualEmails.join(', ')}`);
      return;
    }

    setRecipientListSaving(true);
    try {
      const canCreateFreeTickets = await ensureCurrentStaffCanCreateFreeTickets();
      if (!canCreateFreeTickets) return;

      const saveList = httpsCallable(functions, 'saveFreeTicketRecipientList');
      const result = await saveList({
        listId: selectedRecipientListId,
        name: newListName.trim() || selectedRecipientList.name,
        recipients: recipientDetails
      });
      await loadRecipientLists();
      setSelectedRecipientListId(result.data?.id || selectedRecipientListId);
      setSelectedListEmails(result.data?.recipientEmails || emailRecipients);
      setManualEmails('');
      setSelectedCustomerEmails([]);
      setNewListName('');
      setMessageType('success');
      setSuccessMessage('Lista de destinatarios actualizada.');
    } catch (error) {
      console.error('Error actualizando lista de destinatarios:', error);
      setMessageType('error');
      setSuccessMessage(mapCallableError(error));
    } finally {
      setRecipientListSaving(false);
    }
  };

  const handleDeleteRecipientList = async () => {
    if (!selectedRecipientListId) return;
    setRecipientListSaving(true);
    try {
      const canCreateFreeTickets = await ensureCurrentStaffCanCreateFreeTickets();
      if (!canCreateFreeTickets) return;

      const deleteList = httpsCallable(functions, 'deleteFreeTicketRecipientList');
      await deleteList({ listId: selectedRecipientListId });
      setSelectedRecipientListId('');
      setSelectedListEmails([]);
      await loadRecipientLists();
      setMessageType('success');
      setSuccessMessage('Lista de destinatarios eliminada.');
    } catch (error) {
      console.error('Error eliminando lista de destinatarios:', error);
      setMessageType('error');
      setSuccessMessage(mapCallableError(error));
    } finally {
      setRecipientListSaving(false);
    }
  };

  const handleRemoveEmailFromSelectedList = (email) => {
    setSelectedListEmails((prev) => prev.filter((item) => item !== email));
  };

  return (
    <div className="freeTicketCreator-page">
      <section className="freeTicketCreator-hero">
        <div>
          <span className="freeTicketCreator-eyebrow">Invitaciones</span>
          <h2>Crear tickets free</h2>
          <p>Generá cortesías con QR único para invitados, prensa o acciones especiales.</p>
        </div>

        <div className="freeTicketCreator-stats">
          <div className="freeTicketCreator-stat">
            <span>{products.length}</span>
            <small>Productos disponibles</small>
          </div>
          <div className="freeTicketCreator-stat">
            <span>{selectedTicketCount}</span>
            <small>Tickets a crear</small>
          </div>
        </div>
      </section>

      <div className="freeTicketCreator-layout">
        <section className="freeTicketCreator-card">
          <div className="freeTicketCreator-cardHeader">
            <div>
              <span className="freeTicketCreator-step">Paso 1</span>
              <h3>Elegí los productos</h3>
            </div>
            <span className="freeTicketCreator-count">{selectedProducts.length} seleccionados</span>
          </div>

          <label className="freeTicketCreator-field">
            <span>Producto</span>
            <select
              onChange={(e) => handleAddToCart(e.target.value)}
              className="freeTicketCreator-input"
              value=""
            >
              <option value="">Seleccionar producto</option>
              {products.map(product => (
                <option key={`${product.collection}:${product.id}`} value={`${product.collection}:${product.id}`}>
                  {product.model} · {product.collection === 'otherProducts' ? 'Consumo' : 'Entrada'}
                </option>
              ))}
            </select>
          </label>

          <div className="freeTicketCreator-selectedList">
            {selectedProducts.length === 0 && (
              <div className="freeTicketCreator-empty">
                <span>Sin productos seleccionados</span>
                <p>Agregá uno o más productos para habilitar la creación de tickets.</p>
              </div>
            )}

            {selectedProducts.map(product => (
              <div key={`${product.collection}:${product.id}`} className="freeTicketCreator-item">
                <div className="freeTicketCreator-itemInfo">
                  <span>{product.model}</span>
                  <small>{product.collection === 'otherProducts' ? 'Consumo asociado al evento activo' : 'Cantidad de accesos'}</small>
                </div>
                <input
                  type="number"
                  value={product.quantity}
                  min="1"
                  onChange={(e) => handleQuantityChange(product.id, product.collection, parseInt(e.target.value, 10))}
                  className="quantity-input"
                  aria-label={`Cantidad para ${product.model}`}
                />
                <button
                  type="button"
                  className="freeTicketCreator-remove"
                  onClick={() => handleRemoveFromCart(product.id, product.collection)}
                >
                  Eliminar
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="freeTicketCreator-card freeTicketCreator-card--accent">
          <div className="freeTicketCreator-cardHeader">
            <div>
              <span className="freeTicketCreator-step">Paso 2</span>
              <h3>Datos del invitado</h3>
            </div>
          </div>

          <label className="freeTicketCreator-field">
            <span>Nombre opcional</span>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="freeTicketCreator-input"
              placeholder="Ej: Invitado especial"
            />
          </label>

          <div className="freeTicketCreator-summary">
            <span>Resumen</span>
            <strong>{selectedTicketCount} tickets free</strong>
            <small>Nombre asignado: {name || 'Invitado'}</small>
          </div>

          <button
            type="button"
            onClick={handleCreateFreeTickets}
            className="freeTicketCreator-button"
            disabled={isLoading || selectedProducts.length === 0}
          >
            {isLoading
              ? emailRecipients.length > 0 ? 'Creando y enviando...' : 'Creando tickets...'
              : emailRecipients.length > 0 ? 'Crear y enviar tickets' : 'Crear tickets gratuitos'}
          </button>

          {successMessage && (
            <p className={`freeTicketCreator-message freeTicketCreator-message--${messageType}`}>
              {successMessage}
            </p>
          )}
        </section>

        <section className="freeTicketCreator-card freeTicketCreator-emailCard">
          <div className="freeTicketCreator-cardHeader">
            <div>
              <span className="freeTicketCreator-step">Opcional</span>
              <h3>Enviar tickets por email</h3>
            </div>
            <span className="freeTicketCreator-count">{emailRecipients.length} destinatarios</span>
          </div>

          {!emailToolsLoaded ? (
            <div className="freeTicketCreator-emailToolsPrompt">
              <div>
                <strong>¿Querés usar clientes o listas guardadas?</strong>
                <p>La carga de contactos es opcional. Podés crear tickets sin email o escribir emails manualmente.</p>
              </div>
              <button
                type="button"
                className="freeTicketCreator-secondaryButton"
                onClick={handleLoadEmailTools}
                disabled={customersLoading || recipientListsLoading}
              >
                {customersLoading || recipientListsLoading ? 'Cargando contactos...' : 'Cargar contactos y listas'}
              </button>
            </div>
          ) : (
            <div className="freeTicketCreator-listManager">
              <label className="freeTicketCreator-field">
                <span>Lista guardada</span>
                <select
                  value={selectedRecipientListId}
                  onChange={(e) => handleRecipientListChange(e.target.value)}
                  className="freeTicketCreator-input"
                  disabled={recipientListsLoading}
                >
                  <option value="">
                    {recipientListsLoading ? 'Cargando listas...' : 'Seleccionar lista'}
                  </option>
                  {recipientLists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name} ({list.recipientEmails.length})
                    </option>
                  ))}
                </select>
                {recipientListsError && (
                  <small className="freeTicketCreator-inlineError">{recipientListsError}</small>
                )}
              </label>

              <div className="freeTicketCreator-listActions">
                <label className="freeTicketCreator-field">
                  <span>Nombre de lista</span>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    className="freeTicketCreator-input"
                    placeholder={selectedRecipientList?.name || 'Ej: Prensa / invitados VIP'}
                  />
                </label>
                <button
                  type="button"
                  className="freeTicketCreator-secondaryButton"
                  onClick={handleSaveRecipientList}
                  disabled={recipientListSaving || emailRecipients.length === 0}
                >
                  {recipientListSaving ? 'Guardando...' : 'Guardar lista'}
                </button>
                <button
                  type="button"
                  className="freeTicketCreator-secondaryButton"
                  onClick={handleUpdateRecipientList}
                  disabled={recipientListSaving || !selectedRecipientListId || emailRecipients.length === 0}
                >
                  Actualizar lista
                </button>
                <button
                  type="button"
                  className="freeTicketCreator-dangerButton"
                  onClick={handleDeleteRecipientList}
                  disabled={recipientListSaving || !selectedRecipientListId}
                >
                  Eliminar lista
                </button>
              </div>
            </div>
          )}

          {emailToolsLoaded && selectedRecipientList && (
            <div className="freeTicketCreator-listPreview">
              <div className="freeTicketCreator-listPreviewHeader">
                <strong>{selectedRecipientList.name}</strong>
                <span>{selectedListEmails.length} emails en edición.</span>
              </div>
              <div className="freeTicketCreator-listChips">
                {selectedListContactDetails.length === 0 && (
                  <span className="freeTicketCreator-muted">La lista quedó vacía. Agregá contactos y actualizala.</span>
                )}
                {selectedListContactDetails.map((recipient) => (
                  <span key={recipient.email} className="freeTicketCreator-listChip">
                    <span>
                      <strong>{recipient.name}</strong>
                      <small>{recipient.email} · {recipient.source}</small>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveEmailFromSelectedList(recipient.email)}
                      aria-label={`Quitar ${recipient.email} de la lista`}
                    >
                      Quitar
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="freeTicketCreator-emailGrid">
            <label className="freeTicketCreator-field">
              <span>Email manual</span>
              <textarea
                value={manualEmails}
                onChange={(e) => setManualEmails(e.target.value)}
                className="freeTicketCreator-input freeTicketCreator-textarea"
                placeholder="email@cliente.com, otro@email.com"
              />
              <small>Podés separar varios emails con coma, espacio o salto de línea.</small>
            </label>

            <div className="freeTicketCreator-clientPicker">
              {emailToolsLoaded ? (
                <>
                  <label className="freeTicketCreator-field">
                    <span>Clientes</span>
                    <input
                      type="search"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="freeTicketCreator-input"
                      placeholder="Buscar por nombre o email"
                    />
                  </label>

                  <div className="freeTicketCreator-clientList">
                    {customersLoading && <p className="freeTicketCreator-muted">Cargando clientes...</p>}
                    {customersError && <p className="freeTicketCreator-message freeTicketCreator-message--error">{customersError}</p>}
                    {!customersLoading && !customersError && filteredEmailCustomers.length === 0 && (
                      <p className="freeTicketCreator-muted">No hay clientes con email para mostrar.</p>
                    )}
                    {!customersLoading && !customersError && filteredEmailCustomers.map((customer) => (
                      <label key={`${customer.id}:${customer.email}`} className="freeTicketCreator-clientOption">
                        <input
                          type="checkbox"
                          checked={selectedCustomerEmails.includes(customer.email)}
                          onChange={() => toggleCustomerEmail(customer.email)}
                        />
                        <span>
                          <strong>{customer.name}</strong>
                          <small>{customer.email} · {customer.source}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <div className="freeTicketCreator-clientList freeTicketCreator-clientList--locked">
                  <p className="freeTicketCreator-muted">Cargá contactos y listas para seleccionar clientes guardados.</p>
                </div>
              )}
            </div>
          </div>

          {invalidManualEmails.length > 0 && (
            <p className="freeTicketCreator-message freeTicketCreator-message--error">
              Emails inválidos: {invalidManualEmails.join(', ')}
            </p>
          )}
        </section>
      </div>

      {qrData.length > 0 && (
        <div className="qr-code-preview">
          <div className="freeTicketCreator-cardHeader">
            <div>
              <span className="freeTicketCreator-step">Listos para usar</span>
              <h3>QR generados</h3>
            </div>
            <span className="freeTicketCreator-count">{qrData.length} códigos</span>
          </div>
          {qrData.map((product, index) => (
            <div key={index} className="qr-code-item">
              <div className="qr-code-container">
                <QRCode value={product.qrValue} size={256} />
              </div>
              <p>{product.nombre}</p>
              <p>{product.model}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FreeTicketCreator;
